import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Paper, Button, Stack } from '@mui/material';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import { viderconnectlogo, vider_logo } from 'assets';

import {
    checkViderConnectAccess,
    launchViderConnect
} from 'api/services/vider-connect';

import RequestViderConnectAccess from 'views/vider-connect/RequestViderConnectAccess';
import useTitle from 'hooks/useTitle';

type AccessStatus = 'APPROVED' | 'SUSPENDED' | 'EXPIRED' | 'REQUESTED' | 'REJECTED' | null;
type UIState = 'LOADING' | AccessStatus;

const ViderConnect = () => {
    const [uiState, setUiState] = useState<UIState>('LOADING');

    useTitle('Vider Connect');

    useEffect(() => {
        const loadState = async () => {
            try {
                const res: any = await checkViderConnectAccess();
                setUiState(res.data.access ?? null);
            } catch (err) {
                console.error(err);
                setUiState(null);
            }
        };

        loadState();
    }, []);

    if (uiState === 'LOADING') {
        return (
            <Box textAlign="center" mt={5}>
                <CircularProgress />
            </Box>
        );
    }

    if (uiState === 'APPROVED') {
        return <VpingLaunch />;
    }

    if (uiState === 'SUSPENDED') {
        return <StatusScreen
            title="Account Deactivated"
            message="Your Vider Connect account has been deactivated. Please contact support to reactivate."
            color="#dc2626"
        />;
    }

    if (uiState === 'EXPIRED') {
        return <StatusScreen
            title="Subscription Expired"
            message="Your Vider Connect subscription has expired. Please renew to continue using the service."
            color="#d97706"
        />;
    }

    if (uiState === 'REQUESTED') {
        return <StatusScreen
            title="Pending Approval"
            message="Your request for Vider Connect access is under review. You'll be notified once approved."
            color="#2563eb"
        />;
    }

    // null or REJECTED — show request access form
    return <RequestViderConnectAccess />;
};

type ConnectorVariant = 'top-left' | 'bottom-left' | 'top-right' | 'bottom-right';

const CONNECTOR_W = 84;
const CONNECTOR_H = 72;

const Connector = ({ variant }: { variant: ConnectorVariant }) => {
    const isLeft = variant === 'top-left' || variant === 'bottom-left';
    const isTop = variant === 'top-left' || variant === 'top-right';

    const x0 = isLeft ? 0 : CONNECTOR_W;
    const x1 = isLeft ? CONNECTOR_W : 0;
    const y0 = isTop ? CONNECTOR_H * 0.3 : CONNECTOR_H * 0.7;
    const y1 = isTop ? CONNECTOR_H * 0.7 : CONNECTOR_H * 0.3;
    const midX = (x0 + x1) / 2;

    const d = `M ${x0} ${y0} C ${midX} ${y0}, ${midX} ${y1}, ${x1} ${y1}`;

    return (
        <svg width={CONNECTOR_W} height={CONNECTOR_H} viewBox={`0 0 ${CONNECTOR_W} ${CONNECTOR_H}`} style={{ display: 'block' }}>
            <path d={d} fill="none" stroke="#7c93ac" strokeWidth={2} strokeDasharray="6 6" strokeLinecap="round" />
        </svg>
    );
};

const FeatureCard = ({
    icon,
    title,
    description
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
}) => (
    <Paper
        elevation={0}
        sx={{
            width: 220,
            p: 2.5,
            borderRadius: 3,
            background: 'transparent',
            boxShadow: '0 12px 28px rgba(15, 35, 64, 0.12)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5
        }}
    >
        <Box sx={{ color: '#182F53', display: 'flex', flexShrink: 0, mt: 0.25 }}>{icon}</Box>
        <Box>
            <Typography variant="subtitle2" fontWeight={700} color="#182F53" lineHeight={1.25}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.75} lineHeight={1.4}>
                {description}
            </Typography>
        </Box>
    </Paper>
);

const VpingLaunch = () => {
    const [launching, setLaunching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleLaunch = async () => {
        try {
            setLaunching(true);
            setError(null);
            const res: any = await launchViderConnect();
            const url = res?.data?.url;
            if (!url) {
                setError('Failed to get launch URL. Please try again.');
                return;
            }
            window.open(url, '_blank');
        } catch (e) {
            setError('Failed to launch Vider Connect. Please try again.');
        } finally {
            setLaunching(false);
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: 'calc(100vh - 64px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 4,
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'grid',
                    gridTemplateColumns: '220px 84px minmax(380px, 460px) 84px 220px',
                    gridTemplateAreas: `
                        "tl conn1 center conn2 tr"
                        "bl conn3 center conn4 br"
                    `,
                    alignItems: 'center',
                    rowGap: 4
                }}
            >
                <Box sx={{ gridArea: 'tl' }}>
                    <FeatureCard
                        icon={<PeopleAltOutlined fontSize="medium" />}
                        title="One Number, Multiple Users"
                        description="Connect Your Office WhatsApp Number and Enable it for Your Whole Team."
                    />
                </Box>
                <Box sx={{ gridArea: 'bl' }}>
                    <FeatureCard
                        icon={<VisibilityOutlined fontSize="medium" />}
                        title="See Who Sent What"
                        description="Every Message Is Tagged With Sender Details For Complete Transparency."
                    />
                </Box>
                <Box sx={{ gridArea: 'tr' }}>
                    <FeatureCard
                        icon={<LightbulbOutlined fontSize="medium" />}
                        title="Smart Broadcasts & Templates"
                        description="Send Updates Faster With Ready Templates And Bulk Messaging."
                    />
                </Box>
                <Box sx={{ gridArea: 'br' }}>
                    <FeatureCard
                        icon={<EventNoteOutlined fontSize="medium" />}
                        title="Schedule & Automate"
                        description="Schedule Messages And Automate Follow-Ups Effortlessly."
                    />
                </Box>

                <Box sx={{ gridArea: 'conn1', display: 'flex' }}>
                    <Connector variant="top-left" />
                </Box>
                <Box sx={{ gridArea: 'conn3', display: 'flex' }}>
                    <Connector variant="bottom-left" />
                </Box>
                <Box sx={{ gridArea: 'conn2', display: 'flex' }}>
                    <Connector variant="top-right" />
                </Box>
                <Box sx={{ gridArea: 'conn4', display: 'flex' }}>
                    <Connector variant="bottom-right" />
                </Box>

                <Box sx={{ gridArea: 'center' }}>
                    <Paper
                        elevation={8}
                        sx={{
                            borderRadius: 4,
                            overflow: 'hidden',
                            border: '2px solid #7fd858',
                            textAlign: 'center',
                            background: '#fff'
                        }}
                    >
                        <Box sx={{ py: 1.25, background: '#eef3fb' }}>
                            <img src={vider_logo} alt="Vider" style={{ height: 22, objectFit: 'contain' }} />
                        </Box>

                        <Box sx={{ px: 4, pb: 4, pt: 3 }}>
                            <Stack alignItems="center" spacing={1.5}>
                                <img src={viderconnectlogo} alt="Vider Connect" style={{ width: 84, height: 84, display: 'block' }} />

                                <Box
                                    sx={{
                                        display: 'inline-block',
                                        px: 1.5,
                                        py: 0.25,
                                        borderRadius: 5,
                                        background: '#e6f7ee',
                                        color: '#1c9a5b',
                                        fontSize: 12,
                                        fontWeight: 600
                                    }}
                                >
                                    INTRODUCING
                                </Box>

                                <Typography variant="subtitle2" fontWeight={700} color="#182F53" letterSpacing={1}>
                                    VIDER <Box component="span" sx={{ color: '#22a559' }}>CONNECT</Box>
                                </Typography>
                            </Stack>

                            <Box sx={{ width: 56, height: 3, background: '#22a559', mx: 'auto', my: 1.5, borderRadius: 2 }} />

                            <Typography color="text.secondary" sx={{ px: 1 }}>
                                Collaborate On WhatsApp Like Never Before.
                                <br />
                                One Number. Your Whole Team.
                            </Typography>

                            <Stack direction="row" justifyContent="center" spacing={4} mt={3.5}>
                                <Stack alignItems="center" spacing={0.5}>
                                    <PeopleAltOutlined sx={{ color: '#182F53' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Team
                                        <br />
                                        Collaborate
                                    </Typography>
                                </Stack>
                                <Stack alignItems="center" spacing={0.5}>
                                    <ForumOutlined sx={{ color: '#182F53' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Real-Time
                                        <br />
                                        Chat
                                    </Typography>
                                </Stack>
                                <Stack alignItems="center" spacing={0.5}>
                                    <LocationOnOutlined sx={{ color: '#182F53' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Employee
                                        <br />
                                        Tracking
                                    </Typography>
                                </Stack>
                                <Stack alignItems="center" spacing={0.5}>
                                    <VerifiedUserOutlined sx={{ color: '#182F53' }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Secure &
                                        <br />
                                        Reliable
                                    </Typography>
                                </Stack>
                            </Stack>

                            {error && (
                                <Typography mt={2} color="error" variant="body2">{error}</Typography>
                            )}

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleLaunch}
                                disabled={launching}
                                sx={{
                                    mt: 3.5,
                                    py: 1.4,
                                    borderRadius: 2.5,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    background: '#0f2340',
                                    '&:hover': { background: '#0a1a30' }
                                }}
                            >
                                {launching ? <CircularProgress size={22} color="inherit" /> : 'Launch Vider Connect →'}
                            </Button>

                            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                                🔒 Secure. Smart. Build for Teams.
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

const StatusScreen = ({ title, message, color }: { title: string; message: string; color: string }) => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 64px)">
        <Paper elevation={6} sx={{ width: 480, borderRadius: 5, p: 5, textAlign: 'center', background: '#f8fafc' }}>
            <img src={viderconnectlogo} alt="logo" style={{ width: 100, height: 100 }} />
            <Typography variant="h4" fontWeight={700} color={color} mt={2}>
                {title}
            </Typography>
            <Typography mt={2} color="text.secondary">
                {message}
            </Typography>
        </Paper>
    </Box>
);

export default ViderConnect;
